// components/AdminTable.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiUser,
  FiMail,
  FiPhone,
  FiCalendar,
  FiShield,
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiDownload,
  FiRefreshCw,
  FiInfo,
  FiArrowUp,
  FiArrowDown,
  FiUsers,
  FiUserCheck,
  FiClock,
  FiHash,
  FiActivity,
  FiAward,
  FiCircle,
  FiWifi,
  FiWifiOff,
  FiDatabase,
  FiMenu,
  FiX,
  FiFilter,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import Dexie from 'dexie';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';
import { Link } from 'react-router-dom';

// ==================== BASE DE DONNÉES & UTILITAIRES ====================
const db = new Dexie('AdminTableCache');
db.version(1).stores({
  admins: 'id, nom, prenom, email, tel, fonction, sexe, status, cacheId',
  cacheInfo: 'key',
  requestLog: '++id, timestamp, endpoint',
});

class RateLimiter {
  constructor(maxRequests = 5, timeWindow = 60000) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindow;
    this.requests = [];
    this.requestQueue = [];
    this.isProcessing = false;
  }

  async acquire() {
    return new Promise((resolve) => {
      this.requestQueue.push(resolve);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      const now = Date.now();
      this.requests = this.requests.filter(time => now - time < this.timeWindow);

      if (this.requests.length < this.maxRequests) {
        const resolve = this.requestQueue.shift();
        this.requests.push(now);
        resolve();
      } else {
        const oldestRequest = this.requests[0];
        const waitTime = this.timeWindow - (now - oldestRequest);
        await new Promise(resolve => setTimeout(resolve, waitTime + 100));
      }
    }

    this.isProcessing = false;
  }
}

const fetchWithRetry = async (url, options = {}, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
};

const rateLimiter = new RateLimiter(3, 60000);

const logRequest = async (endpoint) => {
  try {
    await db.requestLog.add({ endpoint, timestamp: new Date().toISOString() });
    const count = await db.requestLog.count();
    if (count > 100) {
      const oldest = await db.requestLog.orderBy('id').first();
      if (oldest) await db.requestLog.delete(oldest.id);
    }
  } catch (error) {
    console.error('Erreur logging:', error);
  }
};

const isCacheValid = async () => {
  try {
    const cacheInfo = await db.cacheInfo.get('admin_data');
    if (!cacheInfo) return false;
    const now = new Date();
    const cacheDate = new Date(cacheInfo.timestamp);
    const hoursDiff = (now - cacheDate) / (1000 * 60 * 60);
    return hoursDiff < 24;
  } catch (error) {
    console.error('Erreur cache:', error);
    return false;
  }
};

const saveToCache = async (admins) => {
  try {
    await db.admins.clear();
    const adminsWithId = admins.map((admin, index) => ({ ...admin, cacheId: index + 1 }));
    if (adminsWithId.length > 0) {
      const batchSize = 50;
      for (let i = 0; i < adminsWithId.length; i += batchSize) {
        const batch = adminsWithId.slice(i, i + batchSize);
        await db.admins.bulkAdd(batch);
      }
    }
    await db.cacheInfo.put({ key: 'admin_data', timestamp: new Date().toISOString() });
  } catch (error) {
    console.error('Erreur sauvegarde cache:', error);
  }
};

const loadFromCache = async () => {
  try {
    const admins = await db.admins.toArray();
    return admins;
  } catch (error) {
    console.error('Erreur chargement cache:', error);
    return [];
  }
};

const clearCache = async () => {
  try {
    await db.admins.clear();
    await db.cacheInfo.clear();
  } catch (error) {
    console.error('Erreur vidage cache:', error);
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date invalide';
    return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  } catch (error) {
    return 'Date invalide';
  }
};

const formatDateCache = (dateString) => {
  if (!dateString) return 'Jamais';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date invalide';
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(date);
  } catch (error) {
    return 'Date invalide';
  }
};

const calculateAge = (dateNaissance) => {
  if (!dateNaissance) return null;
  try {
    const today = new Date();
    const birthDate = new Date(dateNaissance);
    if (isNaN(birthDate.getTime())) return null;
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  } catch (error) {
    return null;
  }
};

// ==================== COMPOSANTS RÉUTILISABLES ====================

const Badge = ({ children, color = 'blue' }) => {
  const colors = {
    red: 'bg-red-500/10 border-red-500/20 text-red-400',
    green: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${colors[color] || colors.blue}`}>
      {children}
    </span>
  );
};

// Carte admin mobile
const AdminCard = ({ admin, onViewDetails }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      layout
      className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 shadow-lg"
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <p className="text-blue-400 text-xs font-mono">#{admin.id}</p>
          <p className="text-white font-semibold">{admin.prenom} {admin.nom}</p>
          <div className="flex gap-2 mt-1">
            {admin.fonction === 'SA' ? (
              <Badge color="red"><FiAward size={12} /> Super Admin</Badge>
            ) : (
              <Badge color="blue"><FiShield size={12} /> Admin</Badge>
            )}
          </div>
        </div>
        <div className="text-right">
          {admin.status === 1 ? (
            <Badge color="green"><FiCheckCircle size={12} /> Actif</Badge>
          ) : (
            <Badge color="red"><FiXCircle size={12} /> Inactif</Badge>
          )}
        </div>
      </div>
      <div className="space-y-2 text-sm text-slate-300">
        <div className="flex items-center gap-2"><FiMail className="text-slate-500" size={14} /><span>{admin.email || 'Non renseigné'}</span></div>
        <div className="flex items-center gap-2"><FiPhone className="text-slate-500" size={14} /><span>{admin.tel || 'Non renseigné'}</span></div>
        <div className="flex items-center gap-2"><FiActivity className="text-slate-500" size={14} />
          {admin.activation_token === 1 ? (
            <Badge color="green">Activé</Badge>
          ) : (
            <Badge color="red">Désactivé</Badge>
          )}
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t border-white/10 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Sexe</span><span className="text-slate-200">{admin.sexe_display}</span></div>
              {admin.age && <div className="flex justify-between"><span className="text-slate-400">Âge</span><span className="text-slate-200">{admin.age} ans</span></div>}
              <div className="flex justify-between"><span className="text-slate-400">Créé le</span><span className="text-slate-200">{admin.created ? formatDate(admin.created) : 'N/A'}</span></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="flex gap-2 mt-4 pt-3 border-t border-white/10">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 text-xs font-medium transition-colors"
        >
          {expanded ? 'Moins' : 'Plus'}
        </button>
        <button
          onClick={() => onViewDetails(admin)}
          className="flex-1 py-2 rounded-xl bg-blue-500/10 border border-blue-400/20 text-blue-400 text-xs font-medium flex items-center justify-center gap-1 hover:bg-blue-500/20 transition-colors"
        >
          <FiEye size={14} /> Détails
        </button>
      </div>
    </motion.div>
  );
};

// Menu mobile (actions)
const MobileMenu = ({ isOpen, onClose, onRefresh, onClearCache, onExport, isRefreshing }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={onClose}
        />
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed right-0 top-0 h-full w-72 bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border-l border-blue-400/20 shadow-2xl z-50 p-6"
        >
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-white font-bold text-lg">Menu</h3>
            <button onClick={onClose} className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white"><FiX size={18} /></button>
          </div>
          <div className="space-y-4">
            <button onClick={onRefresh} disabled={isRefreshing} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 disabled:opacity-50">
              <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} /> {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
            </button>
            <button onClick={onClearCache} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400">
              <FiDatabase /> Vider le cache
            </button>
            <button onClick={onExport} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-blue-500/10 border border-blue-400/20 text-blue-400">
              <FiDownload /> Exporter CSV
            </button>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// Filtres mobiles
const MobileFilters = ({ isOpen, onClose, filterFonction, setFilterFonction, filterSexe, setFilterSexe, filterStatus, setFilterStatus }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={onClose}
        />
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-blue-950/95 to-slate-900/95 backdrop-blur-xl border-t border-blue-400/20 rounded-t-2xl z-50 p-6"
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-white font-bold text-lg flex items-center gap-2"><FiFilter /> Filtres</h3>
            <button onClick={onClose} className="p-2 rounded-xl bg-white/5 text-slate-400"><FiX size={18} /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-slate-400 text-xs uppercase block mb-2">Rôle</label>
              <select value={filterFonction} onChange={(e) => setFilterFonction(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white">
                <option value="all">Tous</option>
                <option value="SA">Super Admins</option>
                <option value="ADM">Administrateurs</option>
              </select>
            </div>
            <div>
              <label className="text-slate-400 text-xs uppercase block mb-2">Sexe</label>
              <select value={filterSexe} onChange={(e) => setFilterSexe(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white">
                <option value="all">Tous</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
            </div>
            <div>
              <label className="text-slate-400 text-xs uppercase block mb-2">Statut</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white">
                <option value="all">Tous</option>
                <option value="1">Actifs</option>
                <option value="0">Inactifs</option>
              </select>
            </div>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// ==================== COMPOSANT PRINCIPAL ====================
const AdminTable = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cacheDate, setCacheDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filterFonction, setFilterFonction] = useState('all');
  const [filterSexe, setFilterSexe] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const lastFetchRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Détection mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Connexion Internet
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const debouncedFetch = useCallback((callback, delay = 1000) => {
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(callback, delay);
  }, []);

  const fetchFromAPI = useCallback(async (forceRefresh = false) => {
    try {
      if (!forceRefresh && lastFetchRef.current) {
        const timeSinceLastFetch = Date.now() - lastFetchRef.current;
        if (timeSinceLastFetch < 10000) {
          const cachedAdmins = await loadFromCache();
          if (cachedAdmins && cachedAdmins.length > 0) {
            setAdmins(cachedAdmins);
            return;
          }
        }
      }

      await rateLimiter.acquire();
      await logRequest('Utilisateurs.php');

      const response = await fetchWithRetry(`${API_CONFIG.apiUrl}Utilisateurs.php`);
      const data = await response.json();

      if (data.status === 200) {
        const formattedData = data.data.map(admin => ({
          ...admin,
          fonction_display: admin.fonction === 'SA' ? 'Super Administrateur' : 'Administrateur',
          sexe_display: admin.sexe === 'M' ? 'Masculin' : admin.sexe === 'F' ? 'Féminin' : 'Non spécifié',
          activation_display: admin.activation_token === 1 ? 'Activé' : 'Non activé',
          age: admin.dn ? calculateAge(admin.dn) : null,
        }));
        setAdmins(formattedData);
        await saveToCache(formattedData);
        lastFetchRef.current = Date.now();
        const cacheInfo = await db.cacheInfo.get('admin_data');
        if (cacheInfo) setCacheDate(cacheInfo.timestamp);
      } else {
        throw new Error(data.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      if (err.message.includes('429') || err.status === 429) {
        setError("Trop de requêtes. Veuillez patienter.");
        setTimeout(() => fetchFromAPI(false), 30000);
      } else {
        const cachedAdmins = await loadFromCache();
        if (cachedAdmins && cachedAdmins.length > 0) {
          setAdmins(cachedAdmins);
          setError(null);
        } else {
          setError('Impossible de charger les données.');
        }
      }
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const cacheValid = await isCacheValid();
      if (cacheValid) {
        const cachedAdmins = await loadFromCache();
        if (cachedAdmins && cachedAdmins.length > 0) {
          setAdmins(cachedAdmins);
          const cacheInfo = await db.cacheInfo.get('admin_data');
          if (cacheInfo) setCacheDate(cacheInfo.timestamp);
          if (isOnline) debouncedFetch(() => fetchFromAPI(false), 15000);
          return;
        }
      }
      await fetchFromAPI(false);
    } catch (err) {
      const cachedAdmins = await loadFromCache();
      if (cachedAdmins && cachedAdmins.length > 0) {
        setAdmins(cachedAdmins);
        setError(null);
      } else {
        setError('Impossible de charger les données. Vérifiez votre connexion.');
      }
    } finally {
      setLoading(false);
    }
  };

  const manualRefresh = async () => {
    if (!isOnline) {
      setError('Pas de connexion internet.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (isRefreshing) return;
    setIsRefreshing(true);
    setError(null);
    try {
      await fetchFromAPI(true);
    } catch (err) {
      setError('Erreur lors du rafraîchissement');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsRefreshing(false);
    }
  };

  const clearCacheAndReload = async () => {
    await clearCache();
    lastFetchRef.current = null;
    await fetchFromAPI(true);
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Nom', 'Prénom', 'Email', 'Téléphone', 'Fonction', 'Sexe', 'Date Naissance', 'Âge', 'Status', 'Activation', 'Date Création'];
    const csvData = sortedAdmins.map(admin => [
      admin.id, admin.nom, admin.prenom, admin.email || '', admin.tel || '',
      admin.fonction === 'SA' ? 'Super Admin' : 'Administrateur',
      admin.sexe_display,
      admin.dn ? formatDate(admin.dn) : '', admin.age || '',
      admin.status === 1 ? 'Actif' : 'Inactif',
      admin.activation_token === 1 ? 'Activé' : 'Désactivé',
      admin.created ? formatDate(admin.created) : ''
    ]);
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `utilisateurs_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  useEffect(() => {
    loadData();
    return () => { if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current); };
  }, []);

  // Filtrage / recherche avec debounce
  const [filteredData, setFilteredData] = useState([]);
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      let filtered = [...admins];
      if (searchTerm) {
        filtered = filtered.filter(admin =>
          admin.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          admin.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          admin.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          admin.tel?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      if (filterFonction !== 'all') filtered = filtered.filter(admin => admin.fonction === filterFonction);
      if (filterSexe !== 'all') filtered = filtered.filter(admin => admin.sexe === filterSexe);
      if (filterStatus !== 'all') filtered = filtered.filter(admin => admin.status === parseInt(filterStatus));
      setFilteredData(filtered);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchTerm, admins, filterFonction, filterSexe, filterStatus]);

  // Tri
  const sortedAdmins = useMemo(() => {
    const sortable = [...filteredData];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (sortConfig.key === 'created' || sortConfig.key === 'dn') {
          aValue = aValue ? new Date(aValue) : new Date(0);
          bValue = bValue ? new Date(bValue) : new Date(0);
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredData, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedAdmins.length / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = indexOfFirstItem + itemsPerPage;
  const currentAdmins = sortedAdmins.slice(indexOfFirstItem, indexOfLastItem);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Statistiques
  const stats = useMemo(() => ({
    total: admins.length,
    superAdmins: admins.filter(a => a.fonction === 'SA').length,
    admins: admins.filter(a => a.fonction === 'ADM').length,
    actifs: admins.filter(a => a.status === 1).length,
    inactifs: admins.filter(a => a.status === 0).length,
  }), [admins]);

  // ==================== RENDU ====================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950 relative">
      {/* Fond décoratif */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto p-3 md:p-6 pb-24 md:pb-6">
        {/* Barre d'info cache */}
        <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-3 md:p-4 mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs md:text-sm text-slate-300">
          <div className="flex items-center gap-2 flex-wrap">
            <FiDatabase className="text-blue-400" />
            <span>{cacheDate ? `📦 Données du ${formatDateCache(cacheDate)}` : '🔄 Données fraîches'}</span>
            <FiClock className="text-amber-400 ml-2" />
            <span>Cache 24h</span>
          </div>
          <button onClick={clearCacheAndReload} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-xl text-xs transition-colors">
            Vider cache
          </button>
        </div>

        {/* Header Desktop */}
        <div className="hidden md:block">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-6 mb-6"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                  Gestion des Utilisateurs
                </h1>
                <p className="text-slate-400 text-sm mt-1">Liste des administrateurs et utilisateurs</p>
              </div>
              <div className="flex gap-3">
                <button onClick={manualRefresh} disabled={isRefreshing} className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl text-sm disabled:opacity-50 hover:bg-amber-500/20 transition-colors">
                  <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} /> {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
                </button>
                <button onClick={exportToCSV} className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/10 border border-blue-400/20 text-blue-400 rounded-xl text-sm hover:bg-blue-500/20 transition-colors">
                  <FiDownload /> Exporter
                </button>
              </div>
            </div>

            {/* Stats miniatures */}
            <div className="grid grid-cols-4 gap-3 mt-6">
              {[
                { label: 'Total', value: stats.total, icon: FiUsers, color: 'text-blue-400' },
                { label: 'Super Admins', value: stats.superAdmins, icon: FiAward, color: 'text-red-400' },
                { label: 'Admins', value: stats.admins, icon: FiShield, color: 'text-purple-400' },
                { label: 'Actifs', value: stats.actifs, icon: FiUserCheck, color: 'text-emerald-400' },
              ].map((s, i) => (
                <div key={i} className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                  <s.icon className={`mx-auto mb-1 ${s.color}`} size={20} />
                  <div className="text-white font-bold text-lg">{s.value}</div>
                  <div className="text-slate-400 text-xs">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Header Mobile */}
        <div className="md:hidden mb-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-white">Utilisateurs</h1>
            <p className="text-slate-400 text-xs">{stats.total} utilisateurs</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setMobileFiltersOpen(true)} className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300">
              <FiFilter size={18} />
            </button>
            <button onClick={() => setMobileMenuOpen(true)} className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300">
              <FiMenu size={18} />
            </button>
          </div>
        </div>

        {/* Filtres desktop */}
        <div className="hidden md:flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 min-w-[250px]">
            <FiSearch className="absolute left-4 top-3 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher par nom, email, téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-blue-400/20 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
            />
          </div>
          <select value={filterFonction} onChange={(e) => setFilterFonction(e.target.value)} className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white">
            <option value="all">Tous rôles</option>
            <option value="SA">Super Admins</option>
            <option value="ADM">Administrateurs</option>
          </select>
          <select value={filterSexe} onChange={(e) => setFilterSexe(e.target.value)} className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white">
            <option value="all">Tous sexes</option>
            <option value="M">Masculin</option>
            <option value="F">Féminin</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white">
            <option value="all">Tous statuts</option>
            <option value="1">Actifs</option>
            <option value="0">Inactifs</option>
          </select>
        </div>

        {/* Barre de recherche mobile flottante */}
        <div className="md:hidden mb-4">
          <div className="relative">
            <FiSearch className="absolute left-4 top-3 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-blue-400/20 rounded-2xl text-white placeholder-slate-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Tableau / Cartes */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl overflow-hidden"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-blue-400/20 border-t-blue-400 rounded-full animate-spin mb-4" />
              <p className="text-slate-300">Chargement...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <FiInfo className="text-red-400 text-4xl mb-4" />
              <p className="text-red-400 mb-4">{error}</p>
              <button onClick={loadData} className="px-6 py-2 bg-blue-500/10 border border-blue-400/20 text-blue-400 rounded-xl hover:bg-blue-500/20">Réessayer</button>
            </div>
          ) : (
            <>
              {/* Version Desktop – Tableau */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-sm text-slate-400">
                      {[
                        { key: 'id', label: 'ID', icon: FiHash },
                        { key: 'nom', label: 'Nom & Prénom', icon: FiUser },
                        { key: 'email', label: 'Email', icon: FiMail },
                        { key: 'tel', label: 'Téléphone', icon: FiPhone },
                        { key: 'fonction', label: 'Rôle', icon: FiShield },
                        { key: 'sexe', label: 'Sexe', icon: null },
                        { key: 'status', label: 'Statut', icon: FiActivity },
                        { key: 'created', label: 'Créé le', icon: FiCalendar },
                      ].map(({ key, label, icon: Icon }) => (
                        <th
                          key={key}
                          onClick={() => handleSort(key)}
                          className="p-4 cursor-pointer hover:text-white transition-colors group"
                        >
                          <div className="flex items-center gap-2">
                            {Icon && <Icon size={14} />}
                            <span>{label}</span>
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                              {sortConfig.key === key ? (
                                sortConfig.direction === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />
                              ) : (
                                <FiArrowUp size={14} className="text-slate-600" />
                              )}
                            </span>
                          </div>
                        </th>
                      ))}
                      <th className="p-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {currentAdmins.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="p-8 text-center text-slate-500">Aucun utilisateur trouvé</td>
                        </tr>
                      ) : (
                        currentAdmins.map((admin, index) => (
                          <motion.tr
                            key={admin.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ delay: index * 0.02 }}
                            className="border-b border-white/5 hover:bg-white/5 transition-colors text-sm"
                          >
                            <td className="p-4 font-mono text-blue-400">#{admin.id}</td>
                            <td className="p-4 font-medium text-white">
                              <Link to={`/info-user/${admin.tel}`} className="hover:text-blue-400 transition-colors">
                                {admin.nom} {admin.prenom}
                              </Link>
                            </td>
                            <td className="p-4 text-blue-300">{admin.email || '—'}</td>
                            <td className="p-4 text-slate-300">{admin.tel || '—'}</td>
                            <td className="p-4">
                              {admin.fonction === 'SA' ? (
                                <Badge color="red"><FiAward size={12} /> Super Admin</Badge>
                              ) : (
                                <Badge color="blue"><FiShield size={12} /> Admin</Badge>
                              )}
                            </td>
                            <td className="p-4">
                              <span className="flex items-center gap-2">
                                <FiCircle className={admin.sexe === 'M' ? 'text-blue-400' : admin.sexe === 'F' ? 'text-pink-400' : 'text-slate-500'} size={12} />
                                {admin.sexe_display}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="flex gap-2">
                                {admin.status == 1 ? (
                                  <Badge color="green"><FiCheckCircle size={12} /> Actif</Badge>
                                ) : (
                                  <Badge color="red"><FiXCircle size={12} /> Inactif</Badge>
                                )}
                                {admin.activation_token == 1 ? (
                                  <Badge color="green">Activé</Badge>
                                ) : (
                                  <Badge color="red">Désactivé</Badge>
                                )}
                              </div>
                            </td>
                            <td className="p-4 text-slate-300">{admin.created ? formatDate(admin.created) : 'N/A'}</td>
                            <td className="p-4 text-center">
                              <button
                                onClick={() => { setSelectedAdmin(admin); setShowDetailsModal(true); }}
                                className="p-2 rounded-xl bg-blue-500/10 border border-blue-400/20 text-blue-400 hover:bg-blue-500/20 transition-colors"
                                title="Voir détails"
                              >
                                <FiEye size={16} />
                              </button>
                            </td>
                          </motion.tr>
                        ))
                      )}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Version Mobile – Cartes */}
              <div className="md:hidden p-4 space-y-3">
                <AnimatePresence>
                  {currentAdmins.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">Aucun utilisateur trouvé</div>
                  ) : (
                    currentAdmins.map(admin => (
                      <AdminCard
                        key={admin.id}
                        admin={admin}
                        onViewDetails={(a) => { setSelectedAdmin(a); setShowDetailsModal(true); }}
                      />
                    ))
                  )}
                </AnimatePresence>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 border-t border-white/10">
                  <div className="text-sm text-slate-400">
                    {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, sortedAdmins.length)} sur {sortedAdmins.length}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 hover:bg-white/10"><FiChevronsLeft size={16} /></button>
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 hover:bg-white/10"><FiChevronLeft size={16} /></button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;
                      if (pageNum >= 1 && pageNum <= totalPages) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-8 h-8 rounded-lg text-sm font-medium ${
                              currentPage === pageNum
                                ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30'
                                : 'text-slate-400 hover:bg-white/10'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                      return null;
                    })}
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 hover:bg-white/10"><FiChevronRight size={16} /></button>
                    <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 hover:bg-white/10"><FiChevronsRight size={16} /></button>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <span>Afficher</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* Modal Détails */}
      <AnimatePresence>
        {showDetailsModal && selectedAdmin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border border-blue-400/20 rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <FiUser className="text-blue-400" />
                    {selectedAdmin.prenom} {selectedAdmin.nom}
                  </h2>
                  <button onClick={() => setShowDetailsModal(false)} className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white">
                    <FiX size={20} />
                  </button>
                </div>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Informations personnelles</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-slate-500">ID</span><p className="text-white font-mono">#{selectedAdmin.id}</p></div>
                      <div><span className="text-slate-500">Nom complet</span><p className="text-white">{selectedAdmin.nom} {selectedAdmin.prenom}</p></div>
                      <div><span className="text-slate-500">Email</span><p className="text-blue-300">{selectedAdmin.email || '—'}</p></div>
                      <div><span className="text-slate-500">Téléphone</span><p className="text-white">{selectedAdmin.tel || '—'}</p></div>
                      <div><span className="text-slate-500">Sexe</span><p className="text-white">{selectedAdmin.sexe_display}</p></div>
                      {selectedAdmin.age && <div><span className="text-slate-500">Âge</span><p className="text-white">{selectedAdmin.age} ans</p></div>}
                      <div><span className="text-slate-500">Rôle</span><div className="mt-1">{selectedAdmin.fonction === 'SA' ? <Badge color="red">Super Admin</Badge> : <Badge color="blue">Admin</Badge>}</div></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Statut</h3>
                    <div className="flex gap-3">
                      {selectedAdmin.status === 1 ? <Badge color="green">Actif</Badge> : <Badge color="red">Inactif</Badge>}
                      {selectedAdmin.activation_token === 1 ? <Badge color="green">Activé</Badge> : <Badge color="red">Désactivé</Badge>}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Système</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-slate-500">Date création</span><p className="text-white">{selectedAdmin.created ? formatDate(selectedAdmin.created) : 'N/A'}</p></div>
                      {selectedAdmin.heure && <div><span className="text-slate-500">Heure</span><p className="text-white">{selectedAdmin.heure}</p></div>}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Menus mobiles */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onRefresh={manualRefresh}
        onClearCache={clearCacheAndReload}
        onExport={exportToCSV}
        isRefreshing={isRefreshing}
      />
      <MobileFilters
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        filterFonction={filterFonction}
        setFilterFonction={setFilterFonction}
        filterSexe={filterSexe}
        setFilterSexe={setFilterSexe}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
      />

      {/* Indicateur de connexion */}
      <div className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 backdrop-blur-xl border ${
        isOnline ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
      }`}>
        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
        <span>{isOnline ? 'En ligne' : 'Hors ligne'}</span>
      </div>
    </div>
  );
};

export default AdminTable;